// A plain JS event bus used as the implementation behind `$notificationManager`.
//
// Replaces a `new Vue()` instance previously used as an event bus. Preserves the
// existing multi-arg emit semantics so call sites can pass positional arguments
// (`emit('show-toast', msg, isSuccess)`) and listeners receive them positionally
// (`showToast(message, success)`).
//
// This is intentionally a plain object — not a Vue instance — so it carries over
// unchanged when the codebase moves from Vue 2 to Vue 3.

export function createEventBus() {
  const listeners = new Map();

  return {
    on(event, fn) {
      if (!listeners.has(event)) {
        listeners.set(event, new Set());
      }
      listeners.get(event).add(fn);
    },

    off(event, fn) {
      const set = listeners.get(event);
      if (!set) return;
      set.delete(fn);
      if (set.size === 0) {
        listeners.delete(event);
      }
    },

    emit(event, ...args) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[event-bus]', event, ...args);
      }
      const set = listeners.get(event);
      if (!set) return;
      // Snapshot to a fresh array so handlers that call `off` mid-emit
      // don't mutate the set we're iterating. Array.from is used for clarity
      // and defensiveness; babel.config.js pins `@babel/plugin-transform-spread`
      // to `loose: false`, so `[...set]` would also iterate the Set correctly.
      Array.from(set).forEach((fn) => {
        try {
          fn(...args);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`[event-bus] handler for "${event}" threw:`, err);
        }
      });
    },
  };
}
